import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { fieldOnPageBlueSx, selectMenuWhitePaperProps } from '../mui/fieldOnPageBlue';
import { authService } from '../services/auth';
import { getPortalAccess, resolveProviderId, canLoadProviderScopedData } from '../utils/portalAccess';
import { Visit, Client, Service, Provider, VisitParentWithVisits } from '../types';
import GroupVisitForm from '../components/GroupVisitForm';
import AppDateField from '../components/AppDateField';
import { getClientDisplayName, loadClientsForRecords } from '../utils/clientDisplay';

interface VisitsProps {
  selectedProvider?: number | null;
}

const formatVisitDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not scheduled';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;
    }
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch {
    return 'Invalid date';
  }
};

const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid time';
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  } catch {
    return 'Invalid time';
  }
};

const Visits: React.FC<VisitsProps> = ({ selectedProvider }) => {
  const [visitOccurrences, setVisitOccurrences] = useState<VisitParentWithVisits[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientId: '',
    status: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requiresProviderSelection, setRequiresProviderSelection] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const access = await getPortalAccess();
        setIsAdmin(access.isAdmin);
        setRequiresProviderSelection(access.requiresProviderSelection);
        await Promise.all([fetchVisits(), fetchProviders()]);
      } catch {
        setIsAdmin(false);
        setRequiresProviderSelection(false);
      }
    })();
  }, [selectedProvider]);

  useEffect(() => {
    fetchVisits();
  }, [skip, filters, selectedProvider]);

  const fetchVisits = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const currentUser = await authService.getCurrentUser();
      const access = await getPortalAccess();
      const providerId = resolveProviderId(
        access,
        selectedProvider,
        currentUser.attributes['custom:provider_id'],
      );

      if (!canLoadProviderScopedData(access, providerId) && !(access.isAdmin && providerId == null)) {
        setVisitOccurrences([]);
        setTotal(0);
        return;
      }

      const filterParams = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        clientId: filters.clientId ? Number(filters.clientId) : undefined,
        status: filters.status || undefined,
      };
      const parentFilters = { ...filterParams };
      let response;
      if (access.isAdmin && providerId == null) {
        response = await api.visitParents.list(skip, limit, parentFilters);
      } else if (providerId != null) {
        response = await api.visitParents.list(skip, limit, { ...parentFilters, providerId });
      } else {
        setVisitOccurrences([]);
        setTotal(0);
        return;
      }
      const items = response?.items || [];
      setVisitOccurrences(items);
      setTotal(response?.total ?? items.length);
      const visitsArray = items.flatMap((o: VisitParentWithVisits) => o.visits || []);
      await fetchClientsForVisits(visitsArray);
      await fetchServicesForVisits(visitsArray);
    } catch (err) {
      console.error('Error fetching visits:', err);
      setError('Failed to load visits. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await api.providers.list(0, 100);
      setProviders(response);
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  };

  const fetchClientsForVisits = async (visitsData: Visit[]) => {
    try {
      if (visitsData.length === 0) {
        setClients([]);
        return;
      }
      const access = await getPortalAccess();
      const currentUser = await authService.getCurrentUser();
      const providerId = resolveProviderId(
        access,
        selectedProvider,
        currentUser.attributes['custom:provider_id'],
      );
      const loadedClients = await loadClientsForRecords(visitsData, {
        providerId,
        listByProvider: api.clients.listByProvider,
      });
      setClients(loadedClients);
    } catch {
      setClients([]);
    }
  };

  const fetchServicesForVisits = async (visitsData: Visit[]) => {
    try {
      const serviceIds = Array.from(
        new Set(visitsData.map(v => v.service_id).filter((id): id is number | string => id != null && id !== ''))
      );
      if (serviceIds.length === 0) {
        setServices([]);
        return;
      }
      const allServicesResponse = await api.services.list(0, 1000);
      const allServices = Array.isArray(allServicesResponse)
        ? allServicesResponse
        : (allServicesResponse as { items?: Service[] })?.items || [];
      setServices(allServices.filter((s: Service) => serviceIds.includes(s.service_id)));
    } catch {
      setServices([]);
    }
  };

  const handleFilterChange = (field: keyof typeof filters) => (event: { target: { value: string } }) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
    setSkip(0);
  };

  const clearFilters = () => {
    setFilters({ startDate: '', endDate: '', clientId: '', status: '' });
    setSkip(0);
  };

  const handleCreateVisit = () => {
    setEditingVisit(null);
    setOpenDialog(true);
  };

  const handleEditVisit = (visit: Visit) => {
    setEditingVisit(visit);
    setOpenDialog(true);
  };

  const handleDeleteVisitOccurrence = async (visitParentId: number) => {
    if (window.confirm('Delete this visit occurrence and all client line items for it?')) {
      try {
        await api.visitParents.delete(visitParentId);
        fetchVisits();
      } catch (err) {
        console.error('Error deleting visit occurrence:', err);
        setError('Failed to delete visit occurrence. Please try again.');
      }
    }
  };

  const getProviderName = (id: number | string) => {
    const p = providers.find(x => x.provider_id === id);
    return p ? `${p.first_name} ${p.last_name}` : `Provider ${id}`;
  };

  const getClientName = (id: number | string) => getClientDisplayName(clients, id);

  const getServiceCode = (id: number | string) => {
    const s = services.find(x => x.service_id === id);
    return s?.code || 'N/A';
  };

  if (isLoading && visitOccurrences.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Visits
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateVisit}>
          Add Visit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Visits
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="Start Date"
                value={filters.startDate || null}
                onChange={v => setFilters(prev => ({ ...prev, startDate: v || '' }))}
                usePageBlueStyle
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="End Date"
                value={filters.endDate || null}
                onChange={v => setFilters(prev => ({ ...prev, endDate: v || '' }))}
                usePageBlueStyle
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.clientId}
                  onChange={handleFilterChange('clientId')}
                  label="Client"
                  sx={fieldOnPageBlueSx}
                  MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">
                    <em>All Clients</em>
                  </MenuItem>
                  {clients.map(client => (
                    <MenuItem key={String(client.client_id)} value={client.client_id}>
                      {`${client.first_name} ${client.last_name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                  sx={fieldOnPageBlueSx}
                  MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">
                    <em>All Statuses</em>
                  </MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={clearFilters}
                sx={{ mt: 1, backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              '& th': {
                backgroundColor: '#2D4B73',
                color: 'white',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              },
              '& td': {
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Ratio</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time in</TableCell>
                <TableCell>Time out</TableCell>
                <TableCell>Service Units</TableCell>
                <TableCell>UI (Y/N)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitOccurrences.map(occ => {
                const visit = [...(occ.visits || [])].sort((a, b) => Number(a.visit_id) - Number(b.visit_id))[0];
                if (!visit) return null;
                const clientLabel = (occ.visits || []).map(v => getClientName(Number(v.client_id))).join(', ');
                const ratioLabels = Array.from(
                  new Set(
                    (occ.visits || [])
                      .map(v => v.ratio)
                      .filter((r): r is string => !!r)
                  )
                );
                return (
                  <TableRow hover key={occ.visit_parent_id}>
                    <TableCell>
                      <Typography variant="body2">{getProviderName(visit.provider_id)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{clientLabel || getClientName(Number(visit.client_id))}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getServiceCode(visit.service_id || '')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ratioLabels.length ? ratioLabels.join(', ') : '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatVisitDate(visit.scheduled_start)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTime(visit.scheduled_start)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTime(visit.scheduled_end)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.service_units ?? 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.unusual_incident_flag ? 'Y' : 'N'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={visit.status}
                        color={
                          visit.status === 'COMPLETED' ? 'success' :
                          visit.status === 'IN_PROGRESS' ? 'warning' :
                          visit.status === 'CANCELLED' ? 'error' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditVisit(visit)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteVisitOccurrence(occ.visit_parent_id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > limit && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Showing {skip + 1}-{Math.min(skip + limit, total)} of {total} visit occurrences
          </Typography>
          <Pagination
            count={Math.ceil(total / limit)}
            page={Math.floor(skip / limit) + 1}
            onChange={(_, newPage) => setSkip((newPage - 1) * limit)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      <GroupVisitForm
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingVisit(null);
        }}
        onSaved={() => {
          const wasEdit = !!editingVisit;
          setSuccess(wasEdit ? 'Visit updated successfully.' : 'Visit created successfully.');
          setTimeout(() => setSuccess(null), 4000);
          fetchVisits();
          setOpenDialog(false);
          setEditingVisit(null);
        }}
        fixedProviderId={
          isAdmin
            ? undefined
            : requiresProviderSelection
              ? (selectedProvider ?? undefined)
              : providers[0]
                ? Number(providers[0].provider_id)
                : undefined
        }
        providers={providers}
        defaultProviderId={selectedProvider ?? null}
        editingVisit={editingVisit}
      />
    </Box>
  );
};

export default Visits;
